/**
 * VENKA ERP — "Applications" page (staff).
 * Lists every form submitted on the website, shows the full answers, and downloads PDF / Excel.
 *
 * Usage (in the ERP routes, behind the existing Staff Login):
 *   <ApplicationsAdmin getAuthHeaders={() => ({ Authorization: `Bearer ${staffToken}` })} />
 *
 * Styling: Tailwind classes. No other dependency.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const NO_HEADERS = () => ({});

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/erp/applications";

const STATUS_LABEL = {
  new: "New", contacted: "Contacted", documents_pending: "Documents pending", in_preparation: "In preparation",
  submitted_to_authority: "Submitted to authority", approved: "Approved", refused: "Refused", closed: "Closed",
};
const STATUS_TONE = {
  new: "bg-blue-500/15 text-blue-300", contacted: "bg-slate-500/15 text-slate-300", documents_pending: "bg-amber-500/15 text-amber-300",
  in_preparation: "bg-violet-500/15 text-violet-300", submitted_to_authority: "bg-indigo-500/15 text-indigo-300",
  approved: "bg-emerald-500/15 text-emerald-300", refused: "bg-red-500/15 text-red-300", closed: "bg-slate-500/20 text-slate-400",
};
const COUNTRIES = [["", "All countries"], ["canada", "Canada"], ["portugal", "Portugal"], ["usa", "United States"], ["mexico", "Mexico"],
  ["brazil", "Brazil"], ["panama", "Panama"], ["france", "France"], ["uk", "United Kingdom"], ["romania", "Romania"], ["italy", "Italy"],
  ["netherlands", "Netherlands"], ["ireland", "Ireland"], ["estonia", "Estonia"], ["belgium", "Belgium"], ["germany", "Germany"]];
const PROGRAMS = [["", "All requests"], ["visitor", "Visitor / short stay"], ["short", "Short stay (PT)"], ["transit", "Transit"], ["study", "Study"],
  ["exchange", "Exchange"], ["work", "Work"], ["family", "Family"], ["residence", "Residence"], ["pr", "Permanent residence"],
  ["income", "Income / retirement / nomad"], ["investor", "Investor"], ["startup", "Business / startup"], ["nationality", "Nationality"], ["other", "Other"]];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

async function download(url, headers, fallbackName) {
  const r = await fetch(url, { headers, credentials: "include" });
  if (!r.ok) throw new Error(`Download failed (${r.status})`);
  const cd = r.headers.get("content-disposition") || "";
  const name = decodeURIComponent((cd.match(/filename\*=UTF-8''([^;]+)/) || [])[1] || (cd.match(/filename="([^"]+)"/) || [])[1] || fallbackName);
  const blob = await r.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}

export default function ApplicationsAdmin({ getAuthHeaders = NO_HEADERS, initialOpenId = null }) {
  const authRef = useRef(getAuthHeaders); authRef.current = getAuthHeaders;
  const auth = useCallback(() => authRef.current(), []);
  const [filters, setFilters] = useState({ q: "", country: "", program: "", status: "", from: "", to: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, limit: 25, statuses: Object.keys(STATUS_LABEL) });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(initialOpenId);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const p = new URLSearchParams(qs); p.set("page", page); p.set("limit", 25);
      const r = await fetch(`${API}?${p}`, { headers: auth(), credentials: "include" });
      if (!r.ok) throw new Error(r.status === 401 ? "Your staff session has expired. Log in again." : `Could not load applications (${r.status}).`);
      setData(await r.json());
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [qs, page, auth]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => { setPage(1); setFilters((f) => ({ ...f, [k]: e.target.value })); };
  const pages = Math.max(1, Math.ceil(data.total / (data.limit || 25)));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold">Applications</h1>
          <p className="text-sm text-slate-500">Forms submitted on venkawe.com · {data.total} result{data.total === 1 ? "" : "s"}</p>
        </div>
        <button className="px-4 py-2 rounded-md bg-venka-blue text-white text-sm font-semibold"
          onClick={() => download(`${API}/export.xlsx?${qs}`, auth(), "venka-applications.xlsx").catch((e) => setError(e.message))}>
          Export to Excel
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
        <input className="col-span-2 border rounded-md px-3 py-2 text-sm" placeholder="Search name, email, phone, reference…" value={filters.q} onChange={set("q")} />
        <select className="border rounded-md px-2 py-2 text-sm" value={filters.country} onChange={set("country")}>{COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select className="border rounded-md px-2 py-2 text-sm" value={filters.program} onChange={set("program")}>{PROGRAMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select className="border rounded-md px-2 py-2 text-sm" value={filters.status} onChange={set("status")}>
          <option value="">All statuses</option>{data.statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
        </select>
        <div className="flex gap-1">
          <input type="date" aria-label="From" className="border rounded-md px-1 py-2 text-xs w-1/2" value={filters.from} onChange={set("from")} />
          <input type="date" aria-label="To" className="border rounded-md px-1 py-2 text-xs w-1/2" value={filters.to} onChange={set("to")} />
        </div>
      </div>

      {error && <div className="mb-3 rounded-md bg-red-500/10 text-red-300 px-3 py-2 text-sm">{error}</div>}

      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/40 text-left text-slate-400">
            <tr>{["Reference", "Submitted", "Applicant", "Country", "Request / case", "Office", "Status", "Emails"].map((h) => <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">No applications match these filters.</td></tr>}
            {!loading && data.items.map((a) => {
              const mails = a.emails || [];
              const failed = mails.some((m) => m.status === "failed");
              return (
                <tr key={a.id} className="border-t hover:bg-slate-800/40 cursor-pointer" onClick={() => setOpenId(a.id)}>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{a.reference}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="px-3 py-2"><div className="font-medium">{a.client?.name}</div><div className="text-xs text-slate-500">{a.client?.email}</div></td>
                  <td className="px-3 py-2">{a.country_name}</td>
                  <td className="px-3 py-2"><div>{a.program}</div><div className="text-xs text-slate-500 font-mono">{a.case_type}</div></td>
                  <td className="px-3 py-2 capitalize">{a.office || "—"}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_TONE[a.status] || ""}`}>{STATUS_LABEL[a.status] || a.status}</span></td>
                  <td className="px-3 py-2 text-xs">{mails.length === 0 ? <span className="text-slate-400">pending</span> : failed ? <span className="text-red-300 font-semibold">failed</span> : <span className="text-emerald-300">sent</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-2 mt-3 text-sm">
        <button disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-40" onClick={() => setPage((p) => p - 1)}>Previous</button>
        <span>Page {page} of {pages}</span>
        <button disabled={page >= pages} className="px-3 py-1 border rounded disabled:opacity-40" onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>

      {openId && <ApplicationDetail id={openId} auth={auth} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function ApplicationDetail({ id, auth, onClose, onChanged }) {
  const [app, setApp] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  const fetchApp = useCallback(async () => {
    const r = await fetch(`${API}/${id}`, { headers: auth(), credentials: "include" });
    if (!r.ok) { setError(`Could not open this application (${r.status}).`); return; }
    setApp(await r.json());
  }, [id, auth]);
  useEffect(() => { fetchApp(); }, [fetchApp]);

  const patch = async (body, label) => {
    setBusy(label); setError("");
    try {
      const r = await fetch(`${API}/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...auth() }, credentials: "include", body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Update failed (${r.status})`);
      setApp(await r.json()); onChanged();
    } catch (e) { setError(e.message); } finally { setBusy(""); }
  };
  const resend = async (who) => {
    setBusy(`resend-${who}`); setError("");
    try {
      const r = await fetch(`${API}/${id}/resend-emails?who=${who}`, { method: "POST", headers: auth(), credentials: "include" });
      if (!r.ok) throw new Error(`Resend failed (${r.status})`);
      await fetchApp(); onChanged();
    } catch (e) { setError(e.message); } finally { setBusy(""); }
  };
  const dl = (path, name) => download(`${API}/${id}${path}`, auth(), name).catch((e) => setError(e.message));

  const flags = app ? Object.entries(app.routing || {}).filter(([, v]) => v === true || (v && typeof v === "object" && !Array.isArray(v) && v.meets === false)) : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <aside className="w-full max-w-3xl h-full overflow-y-auto bg-background text-slate-100 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {!app ? <div className="p-6">{error || "Loading…"}</div> : (
          <>
            <div className="sticky top-0 bg-venka-blue text-white px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <div className="font-mono text-sm opacity-80">{app.reference}</div>
                <h2 className="text-xl font-bold">{app.client?.name}</h2>
                <div className="text-sm opacity-80">{app.country_name} · {app.program} · <span className="font-mono">{app.case_type}</span></div>
              </div>
              <button className="text-2xl leading-none px-2" aria-label="Close" onClick={onClose}>×</button>
            </div>

            <div className="p-6 space-y-6">
              {error && <div className="rounded-md bg-red-500/10 text-red-300 px-3 py-2 text-sm">{error}</div>}

              <div className="flex flex-wrap gap-2">
                <span className="self-center text-xs font-semibold uppercase tracking-wider opacity-70">PDF</span>
                {["en", "pt", "fr"].map((lg) => (
                  <button key={lg} data-testid={`app-pdf-${lg}`} onClick={() => dl(`/pdf?copy=${lg}`, `${app.reference}-${lg.toUpperCase()}.pdf`)}
                    className={`px-3 py-2 rounded-md text-sm font-semibold ${lg === (app.client?.language || "en") ? "bg-venka-blue text-white" : "border"}`}
                    title={lg === (app.client?.language || "en") ? "Customer's language" : ""}>
                    {{ en: "English", pt: "Português", fr: "Français" }[lg]}{lg === (app.client?.language || "en") ? " ★" : ""}
                  </button>
                ))}
                <button className="px-3 py-2 rounded-md border text-sm font-semibold" onClick={() => dl("/xlsx", `${app.reference}.xlsx`)}>Download Excel</button>
                {app.case_id && <a className="px-3 py-2 rounded-md bg-venka-green text-slate-950 text-sm font-semibold" href={`/portal/processes/${app.case_id}`}>Open case</a>}
              </div>

              <section className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm bg-slate-950/40 rounded-lg p-4">
                {[["Submitted", fmtDate(app.created_at)], ["Email", app.client?.email], ["Phone", `${app.client?.phone || ""}${app.client?.whatsapp === "yes" ? " (WhatsApp)" : ""}`],
                  ["Language", app.client?.language?.toUpperCase()], ["Nationality", app.client?.nationality], ["Office", app.office]].map(([k, v]) => (
                  <div key={k}><div className="text-xs text-slate-500">{k}</div><div className="font-medium break-words">{v || "—"}</div></div>
                ))}
              </section>

              {flags.length > 0 && (
                <section className="flex flex-wrap gap-2">
                  {flags.map(([k]) => <span key={k} className="px-2 py-1 rounded-full bg-amber-500/15 text-amber-200 text-xs font-semibold">{k.replaceAll("_", " ")}</span>)}
                </section>
              )}

              <section className="grid md:grid-cols-3 gap-3 items-end">
                <label className="text-sm">Status
                  <select className="mt-1 w-full border rounded-md px-2 py-2" value={app.status} disabled={!!busy} onChange={(e) => patch({ status: e.target.value }, "status")}>
                    {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </label>
                <label className="text-sm">Assigned to
                  <input className="mt-1 w-full border rounded-md px-2 py-2" defaultValue={app.assigned_to || ""} onBlur={(e) => e.target.value !== (app.assigned_to || "") && patch({ assigned_to: e.target.value }, "assign")} placeholder="Consultant name" />
                </label>
                <div className="text-xs text-slate-500">{busy ? "Saving…" : `Updated ${fmtDate(app.updated_at)}`}</div>
              </section>

              <section>
                <h3 className="font-semibold mb-2">Emails</h3>
                <ul className="text-sm space-y-1">
                  {(app.emails || []).length === 0 && <li className="text-slate-500">Being sent…</li>}
                  {(app.emails || []).map((m, i) => (
                    <li key={i} className="flex flex-wrap gap-2"><span className={m.status === "sent" ? "text-emerald-300" : "text-red-300"}>{m.status}</span>
                      <span className="font-medium">{m.kind}</span><span className="text-slate-500">{(m.to || []).join(", ")} · {fmtDate(m.at)}</span>
                      {m.error && <span className="text-red-300 text-xs w-full">{m.error}</span>}</li>
                  ))}
                </ul>
                <div className="flex gap-2 mt-2">
                  <button className="px-3 py-1 border rounded text-xs" disabled={!!busy} onClick={() => resend("customer")}>Resend to customer</button>
                  <button className="px-3 py-1 border rounded text-xs" disabled={!!busy} onClick={() => resend("sales")}>Resend to sales</button>
                </div>
              </section>

              {(app.files || []).length > 0 && (
                <section>
                  <h3 className="font-semibold mb-2">Uploaded documents</h3>
                  <ul className="text-sm divide-y border rounded-md">
                    {app.files.map((f) => (
                      <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div><div className="font-medium">{f.filename}</div><div className="text-xs text-slate-500">{f.field} · {Math.round((f.size || 0) / 1024)} KB</div></div>
                        <button className="px-3 py-1 border rounded text-xs" onClick={() => dl(`/files/${f.id}`, f.filename)}>Download</button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section>
                <h3 className="font-semibold mb-2">Answers</h3>
                {(app.summary?.en?.sections || []).map((s) => (
                  <details key={s.title} className="border rounded-md mb-2" open>
                    <summary className="px-3 py-2 font-semibold cursor-pointer bg-slate-950/40">{s.title}</summary>
                    <dl className="px-3 py-2 grid grid-cols-5 gap-x-4 gap-y-1 text-sm">
                      {s.rows.map((r, i) => r.group ? <div key={i} className="col-span-5 mt-2 font-semibold text-xs uppercase tracking-wide text-slate-400">{r.group}</div>
                        : r.sub ? <div key={i} className="col-span-5 mt-1 text-xs font-mono text-slate-500">{r.sub}</div>
                        : [<dt key={`${i}l`} className="col-span-2 text-slate-500">{r.label}</dt>, <dd key={`${i}v`} className="col-span-3 break-words">{r.value}</dd>])}
                    </dl>
                  </details>
                ))}
              </section>

              <section>
                <h3 className="font-semibold mb-2">Notes & history</h3>
                <div className="flex gap-2 mb-2">
                  <input className="flex-1 border rounded-md px-3 py-2 text-sm" placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} />
                  <button className="px-3 py-2 rounded-md bg-venka-blue text-white text-sm" disabled={!note.trim() || !!busy} onClick={() => patch({ note }, "note").then(() => setNote(""))}>Add</button>
                </div>
                <ul className="text-sm space-y-1">
                  {[...(app.notes || []).map((n) => ({ at: n.at, text: `Note: ${n.text}`, by: n.by })), ...(app.history || []).map((h) => ({ at: h.at, text: h.action, by: h.by }))]
                    .sort((a, b) => (a.at < b.at ? 1 : -1))
                    .map((h, i) => <li key={i}><span className="text-slate-500">{fmtDate(h.at)} · {h.by}</span> — {h.text}</li>)}
                </ul>
              </section>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
